import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';

import { IQuestion } from '../../classes/Question';
import { questionListRender, questionListRenderFactory } from '../render/QuestionListRender';
import { IQuestionList } from '../../classes/QuestionsList';
import { ISurvey } from '../../classes/Survey';
import { QuestionService } from '../../services/QuestionService';
import { SurveyService } from '../../services/SurveyService';
import { EmpsSurveyConverter } from '../../classes/data-conversion/SurveyConverter';
import { QuestionsConverter } from '../../classes/data-conversion/QuestionsConverter';
import QuestionsData from './QuestionsData';
import SurveyDashboardOverview from './SurveyDashboardOverview/SurveyDashboardOverview';
import ComparativeInformation from './ComparativeInformation/ComparativeInformation';
import {connect} from "react-redux";
import {addUserThunk, getUserWithEmailThunk} from '../../../redux/users/users.thunk';
import SurveyDashboardOverviewDummyData from "./SurveyDashboardOverview/SurveyDashboardOverviewDummyData";

interface URLParams {
    id: string;
}

interface IState {
    loading: boolean,
    survey: ISurvey,
    questions: IQuestion[],
    errorMessage: string,
}

interface IProps {
    user: any,
    mapStateToProps: any,
    mapDispatchToProps: any,
    getUserEmail: (email: string) => void,
}

const SurveyDashboard = (props: IProps) => {
    let { id } = useParams<URLParams | any>();

    const [state, setState] = useState<IState>({
        loading: false,
        survey: {} as ISurvey,
        questions: [] as IQuestion[],
        errorMessage: "",
    });
    const [viewQuestionsData, setViewQuestionsData] = useState(false);
    const [viewComparative, setViewComparative] = useState(false);
    const [allSurveys, setAllSurveys] = useState([] as any[]);
    const [comparisonsLoaded, setComparisonsLoaded] = useState(false);
    const [titleSurveyMain, setTitleSurveyMain] = useState("");
    const [dummyDataSurveyIds, setDummyDataSurveyIds] = useState([] as string[]);
    const [hasAutoNavigated, setHasAutoNavigated] = useState(false);
    
    // Auto-navigate to questions data for custom surveys (skip overview) - only once on load
    useEffect(() => {
        const templateSurveys = ["Employee Burnout", "Employee Engagement", "Job Satisfaction Survey", "eNPS"];
        const isCustomSurvey = state.survey.title && !templateSurveys.includes(state.survey.title);
        
        if (isCustomSurvey && Object.keys(state.survey).length > 0 && !hasAutoNavigated) {
            setViewQuestionsData(true);
            setHasAutoNavigated(true);
        }
    }, [state.survey, hasAutoNavigated]);
    
    useEffect(() => {
        if (id && props.user && props.user.id) {
            let isCancelled = false;
            
            setState({ ...state, loading: true });
            (async () => {
                try {
                    // Single combined call to avoid duplicate backend requests
                    const { survey: currentSurvey, questions: currentQuestions, title: surveyTitle } = await SurveyService.getSurveyWithQuestions(id);
                    
                    if (isCancelled) return;
                    
                    setState({
                        ...state,
                        loading: false,
                        survey: currentSurvey,
                        questions: currentQuestions,
                    });
                    setTitleSurveyMain(surveyTitle);
                    
                    // Background path - Load comparison data (only for template surveys)
                    const templateSurveys = ["Employee Burnout", "Employee Engagement", "Job Satisfaction Survey", "eNPS"];
                    const isTemplateSurvey = templateSurveys.includes(currentSurvey.title);
                    
                    if (!isTemplateSurvey) {
                        setAllSurveys([]);
                        setComparisonsLoaded(true);
                        return;
                    }
                    
                    try {
                        // Use the raw data already loaded from getSurveyWithQuestions
                        console.log('[DEBUG] Raw survey data from cache');
                        console.log('[DEBUG] Native responses count:', (currentSurvey as any).native_responses?.length || 0);
                        
                        // Log first few responses to see dates
                        if ((currentSurvey as any).native_responses && (currentSurvey as any).native_responses.length > 0) {
                            console.log('[DEBUG] First response (0):', (currentSurvey as any).native_responses[0]);
                            console.log('[DEBUG] Response 16:', (currentSurvey as any).native_responses[16]);
                            console.log('[DEBUG] Last response:', (currentSurvey as any).native_responses[(currentSurvey as any).native_responses.length - 1]);
                        }
                        
                        // Survey already has raw responses attached from getSurveyWithQuestions
                        const surveyWithResponses = currentSurvey;
                        console.log('[DEBUG] Survey with responses:', surveyWithResponses);
                        console.log('[DEBUG] Current survey has questions?', (currentSurvey as any).questions?.length || 0);
                        console.log('[DEBUG] First question:', (currentSurvey as any).questions?.[0]);
                        const questionMapping = (currentSurvey as any).questions?.map((q: any) => ({ id: q.id, dimension: q.dimension }));
                        console.log('[DEBUG] Question dimensions:', questionMapping);
                        console.table(questionMapping);
                        
                        if (!isCancelled) {
                            setAllSurveys([surveyWithResponses]);
                            setComparisonsLoaded(true);
                        }
                    } catch (error) {
                        console.error("[Comparison] Error loading survey data:", error);
                        setAllSurveys([]);
                        setComparisonsLoaded(true);
                    }
                } catch (error) {
                    console.log("Error loading survey:", error);
                    if (!isCancelled) {
                        setState({
                            ...state,
                            loading: false,
                            errorMessage: (error as any).message,
                        });
                    }
                }
            })();
            
            // Cleanup function to prevent state updates if component unmounts
            return () => {
                isCancelled = true;
            };
        }
    }, [id, props.user]);
    console.log(" ******** props.user):   ",props.user);
    const questionLists = (questions: IQuestion[]): IQuestionList[] => {
        let questionLists = [] as IQuestionList[];
        let questionList: IQuestionList = {
            type: "",
            questions: [],
            render: questionListRender,
        };
        let previousType = "";
        for (const question of questions) {
            if (question.type != previousType && previousType != "") {
                questionList.type = previousType;
                questionList.render = questionListRenderFactory(previousType);
                questionLists.push(questionList);
                questionList = {
                    type: "",
                    questions: [],
                    render: questionListRender,
                };
            }
            questionList.questions.push(question);
            previousType = question.type;
        }
        questionList.type = previousType;
        questionList.render = questionListRenderFactory(previousType);
        questionLists.push(questionList);
        return questionLists;
    }

    const { loading, survey, questions, errorMessage } = state;
    console.log("FIle SurveyDashboard.tsx: survey: ",survey);
    console.log("FIle SurveyDashboard.tsx: questions: ",questions);
    console.log("FIle SurveyDashboard.tsx: questions2All: ",allSurveys);
    console.log("FIle SurveyDashboard.tsx: titleSurveyMain: ",titleSurveyMain);
    return (
        <div>
            {errorMessage}
            {loading ? "Loading" : ""}
            {
                Object.keys(survey).length > 0 && questions.length > 0 && (
                    viewQuestionsData && <QuestionsData survey={survey} questionLists={questionLists(questions)} setViewQuestionsData={setViewQuestionsData} />
                    ||
                    viewComparative && <ComparativeInformation 
                        survey={allSurveys.length > 0 ? allSurveys[0] : survey} 
                        questions={allSurveys} 
                        currentSurvey={questions} 
                        titleSurveyCurrent={titleSurveyMain} 
                        setViewComparative={setViewComparative}
                        comparisonsLoaded={comparisonsLoaded}
                    />
                    ||
                    (dummyDataSurveyIds.find((surveyId) => surveyId === survey.id) ? <SurveyDashboardOverviewDummyData survey={survey} questions={questions}
                                                          setViewQuestionsData={setViewQuestionsData}
                                                          setViewComparative={setViewComparative}/> : <SurveyDashboardOverview survey={allSurveys.length > 0 ? allSurveys[0] : survey} questions={questions}
                                                                                                                               setViewQuestionsData={setViewQuestionsData}
                                                                                                                               setViewComparative={setViewComparative}
                                                                                                                               allSurveys={allSurveys}
                                                                                                                               comparisonsLoaded={comparisonsLoaded}/>)
                )
            }
        </div>
    )
}

const mapStateToProps = (state : any) => {
    console.log('state app:', state);
    return {
        user: state.users.user,
        status: state.users.status
    };
};

const mapDispatchToProps = (dispatch : any) => {
    return {
        getUserEmail: (email:string) => dispatch(getUserWithEmailThunk(email)),
        addUser: (obj:any) => dispatch(addUserThunk(obj))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SurveyDashboard);
